// The model's answer, tidied — and the error thrown when it cannot be made to work.
//
// Both lived in `generateWebApp.ts`, which imports the whole prompt. The store imported
// one error CLASS from there and republish imported one helper, and each of those single
// symbols pulled six hundred lines of prompt into the app binary — the very thing moving
// generation to the server was meant to get out of it. A one-line import is not a small
// dependency.

export class WebAppGenerationError extends Error {
  readonly diagnostics: string[];
  readonly appCode: string;
  readonly gateFailures: number;
  constructor(message: string, diagnostics: string[], appCode: string, gateFailures: number) {
    super(message);
    this.name = 'WebAppGenerationError';
    this.diagnostics = diagnostics;
    this.appCode = appCode;
    this.gateFailures = gateFailures;
  }
}

/**
 * Models wrap output in fences despite being told not to, and sometimes emit a whole
 * document when asked for a body. Both are cheap to correct here and expensive to spend a
 * repair round on.
 */
export function normalizeAppCode(raw: string): string {
  let out = raw.trim();

  const fence = out.match(/^```[a-zA-Z]*\n([\s\S]*?)\n?```$/);
  if (fence) out = fence[1].trim();

  // Models narrate. A repair round in particular tends to open with an explanation of what
  // it is about to change, and that prose was being published verbatim into the app — a
  // paragraph of the model reasoning about the checker, rendered above the UI on a live
  // page. Anything before the first real tag is commentary, never app code: the output
  // contract says the first character is markup.
  const firstTag = out.search(/<(?:style|script|section|div|main|header|nav|article|form|h[1-6]|p|ul|ol|table|button|img|span)\b/i);
  if (firstTag > 0) out = out.slice(firstTag).trim();

  // Same for a trailing sign-off after the last closing tag.
  const lastClose = out.lastIndexOf('>');
  if (lastClose > -1 && lastClose < out.length - 1) out = out.slice(0, lastClose + 1).trim();

  // If it came back as a full document, take the body and keep any <style> from the head —
  // dropping the head wholesale would silently discard the app's own theming.
  if (/<html[\s>]/i.test(out) || /<!doctype/i.test(out)) {
    const headStyles = [...out.matchAll(/<style\b[^>]*>[\s\S]*?<\/style>/gi)]
      .filter((m) => {
        const bodyStart = out.search(/<body\b[^>]*>/i);
        return bodyStart === -1 || (m.index ?? 0) < bodyStart;
      })
      .map((m) => m[0])
      .join('\n');
    const body = out.match(/<body\b[^>]*>([\s\S]*?)<\/body>/i);
    if (body) out = `${headStyles}\n${body[1].trim()}`.trim();
  }

  return out;
}

